import { ScanResult, ScanSummary, createScanSummary } from './types';

export function runGameFlowScan(): ScanSummary {
  console.group('🎮 Game Flow Scan');
  
  const results: ScanResult[] = [];

  try {
    const currentPath = window.location.pathname;

    results.push({
      name: 'Current Route',
      status: 'info',
      message: `Current path: ${currentPath}`,
      details: `Full URL: ${window.location.href}`
    });

    // Check 1: Header navigation
    const header = document.querySelector('header');
    const headerLinks = header?.querySelectorAll('a, button');
    
    results.push({
      name: 'Header Navigation',
      status: headerLinks && headerLinks.length > 0 ? 'pass' : 'warn',
      message: headerLinks 
        ? `${headerLinks.length} navigation element(s) in header`
        : 'No navigation elements found in header',
      details: 'Header should provide navigation to key pages'
    });

    // Check 2: Logo home link
    const logo = document.querySelector('[data-wordpop-logo], [class*="logo"], a[href="/"], header a[href="/"]');
    results.push({
      name: 'Logo Home Link',
      status: logo ? 'pass' : 'warn',
      message: logo ? 'Logo link to home found' : 'No logo home link detected',
      details: 'Logo should link to / or /play'
    });

    // Check 3: Settings dialog trigger
    const settingsButton = document.querySelector('button[aria-label*="settings" i], [class*="settings"]');
    results.push({
      name: 'Settings Access',
      status: settingsButton ? 'pass' : 'fail',
      message: settingsButton ? 'Settings button found' : 'Settings button not found',
      details: 'Settings dialog should be accessible from all pages'
    });

    // Check 4: Result modal (if on /play)
    if (currentPath.includes('/play') || currentPath.includes('/puzzle')) {
      const resultModal = document.querySelector('[role="dialog"]');
      results.push({
        name: 'Result Modal',
        status: 'info',
        message: resultModal ? 'Result modal present' : 'Result modal not visible',
        details: 'Shows after game completion'
      });

      // Check 5: Share buttons (in result modal)
      const shareButtons = document.querySelectorAll('[aria-label*="share" i], [aria-label*="copy" i]');
      results.push({
        name: 'Share Options',
        status: shareButtons.length > 0 ? 'pass' : 'info',
        message: shareButtons.length > 0 
          ? `${shareButtons.length} share button(s) found` 
          : 'Share buttons not currently visible',
        details: 'Appear in ResultModal, StatsDialog, and ShareProfile'
      });
    }

    // Check 6: Archive link/button - robust detection with multiple fallbacks
    const archiveButton = document.querySelector(
      'button[aria-label="View puzzle archive"], a[href*="/archive"], a[href*="/puzzle"], [data-archive-link], header button:has([class*="Calendar"])'
    );
    // Also check if we're currently on an archive/puzzle page
    const isOnArchivePage = currentPath.includes('/archive') || currentPath.includes('/puzzle');
    results.push({
      name: 'Archive Navigation',
      status: (archiveButton || isOnArchivePage) ? 'pass' : 'warn',
      message: (archiveButton || isOnArchivePage) ? 'Archive access found' : 'Archive navigation not found',
      details: isOnArchivePage ? 'Currently on archive/puzzle page' : (archiveButton ? 'User should be able to access past puzzles via archive button or link' : 'Consider adding archive navigation')
    });

    // Check 7: Profile link (if authenticated)
    const profileLink = document.querySelector('a[href*="/profile"]');
    const userAvatar = document.querySelector('[aria-label*="profile" i], [aria-label="User menu"], [data-radix-collection-item]');
    
    results.push({
      name: 'Profile Navigation',
      status: profileLink || userAvatar ? 'pass' : 'info',
      message: profileLink || userAvatar 
        ? 'Profile navigation found' 
        : 'Profile link not visible (user may not be logged in)',
      details: 'Only visible for authenticated users'
    });

    // Check 8: Footer links
    const footer = document.querySelector('footer');
    const footerLinks = footer?.querySelectorAll('a');
    
    results.push({
      name: 'Footer Navigation',
      status: footerLinks && footerLinks.length > 0 ? 'pass' : 'warn',
      message: footerLinks 
        ? `${footerLinks.length} link(s) in footer`
        : 'No footer links found',
      details: 'Footer should include About, Help, Privacy, Terms'
    });

    // Check 9: Modal close buttons (Radix UI Dialog structure)
    const modals = document.querySelectorAll('[role="dialog"]');
    const modalsWithClose: number[] = [];
    
    modals.forEach((modal, index) => {
      const closeButton = modal.querySelector(
        'button[aria-label*="close" i], button[class*="DialogClose"], button[data-radix-collection-item], svg.lucide-x'
      );
      if (closeButton) {
        modalsWithClose.push(index);
      }
    });

    results.push({
      name: 'Modal Exit Paths',
      status: modals.length === 0 ? 'info' : modalsWithClose.length === modals.length ? 'pass' : 'fail',
      message: modals.length === 0 
        ? 'No modals currently open'
        : `${modalsWithClose.length}/${modals.length} modal(s) have close buttons`,
      details: 'Every modal should have a way to close (checks for Radix UI DialogClose components)'
    });

    // Check 10: Back navigation
    const backButtons = document.querySelectorAll('button[aria-label*="back" i], a[aria-label*="back" i]');
    results.push({
      name: 'Back Navigation',
      status: backButtons.length > 0 ? 'pass' : 'info',
      message: backButtons.length > 0 
        ? `${backButtons.length} back button(s) found`
        : 'No explicit back buttons (browser back works)',
      details: 'Users can always use browser back button'
    });

    // Check 11: Dead-end detection
    const hasNavigation = (headerLinks && headerLinks.length > 0) || 
                          (footerLinks && footerLinks.length > 0) ||
                          logo;

    results.push({
      name: 'Dead-End Detection',
      status: hasNavigation ? 'pass' : 'fail',
      message: hasNavigation 
        ? 'Page has navigation paths'
        : 'DEAD-END: No navigation found on page',
      details: 'Every page should provide navigation to other areas'
    });

    // Check 12: Game flow completeness
    // Use data attributes for reliable detection, with fallbacks
    const hasAuthAccess = 
      // Primary: data attributes (most reliable)
      document.querySelector('[data-user-menu]') !== null ||
      document.querySelector('[data-sign-in]') !== null ||
      // Fallbacks
      profileLink !== null ||
      userAvatar !== null ||
      document.querySelector('[aria-label="User menu"]') !== null ||
      document.querySelector('button[aria-label="Sign in"]') !== null ||
      document.querySelector('a[href*="/auth"]') !== null;

    const hasArchiveAccess = 
      // Primary: data attribute
      document.querySelector('[data-archive-link]') !== null ||
      // Fallbacks
      archiveButton !== null ||
      isOnArchivePage;
    
    const flowChecks = {
      'Home/Landing': document.querySelector('[data-wordpop-logo], [data-logo-link], a[href="/"], a[href="/play"]') !== null,
      'Play Game': currentPath.includes('/play') || currentPath.includes('/puzzle') || document.querySelector('a[href*="/play"]') !== null,
      'View Stats': settingsButton !== null || document.querySelector('[aria-label*="stats" i]') !== null,
      'Archive Access': hasArchiveAccess,
      'Profile/Auth': hasAuthAccess,
    };

    const completedFlows = Object.values(flowChecks).filter(Boolean).length;
    const totalFlows = Object.keys(flowChecks).length;

    results.push({
      name: 'Game Flow Completeness',
      status: completedFlows === totalFlows ? 'pass' : completedFlows >= totalFlows - 1 ? 'warn' : 'fail',
      message: `${completedFlows}/${totalFlows} core flows accessible`,
      details: Object.entries(flowChecks).map(([name, present]) => 
        `${present ? '✓' : '✗'} ${name}`
      ).join(', ')
    });

  } catch (error) {
    results.push({
      name: 'Game Flow Scan Error',
      status: 'fail',
      message: `Failed to run game flow scan: ${error}`,
      details: String(error)
    });
  }

  const summary = createScanSummary('Game Flow Scan', results);
  
  console.log(`✅ Passed: ${summary.passCount}`);
  console.log(`⚠️  Warnings: ${summary.warnCount}`);
  console.log(`❌ Failed: ${summary.failCount}`);
  console.table(results);
  console.groupEnd();
  
  return summary;
}
